import { Box, Stack, Typography } from "@mui/material";
import { useEffect } from "react";

import SelectSite from "~/components/select/SelectSite";
import { useAppDispatch, useAppSelector } from "~/hooks/reduxHooks";
import useStartingSites from "~/hooks/useStartingSites";
import { BpfTreeType } from "~/services/graphql";
import { type SiteLocation, updateSite, updateSiteRequired } from "~/store/siteSlice";

interface RequireSiteProps {
	children: React.ReactNode;
	/** Set false to opt a page out of requiring a selected site. @default true */
	required?: boolean;
}

/**
 * Gates content that needs a site context. When no site is selected (and one is required), renders
 * the site picker instead of children. The selection is persisted via the Redux `site` slice so it
 * survives reloads.
 *
 * The default picker (`SelectSite` with no `options`) only offers the user's FINISH-tree grants —
 * Finish has no slice-level picker of its own and reads the shared `site` directly, so this gate is
 * the only place that ever selects a site for it. A user with ONLY RETAIL-tree grants (no FINISH
 * grants) would otherwise dead-end here forever, unable to reach ANY feature area. When that's the
 * case, this gate steps aside and renders children immediately, leaving the Retail slice's own
 * RETAIL-tree gate (RetailHome / RetailSelectSite) to resolve the site instead. Users with a FINISH
 * grant (with or without other grants) see the unchanged FINISH-tree picker.
 */
function RequireSite({ children, required = true }: RequireSiteProps) {
	const site = useAppSelector((state) => state.site);
	const dispatch = useAppDispatch();
	const finish = useStartingSites(BpfTreeType.Finish);
	const retail = useStartingSites(BpfTreeType.Retail);

	useEffect(() => {
		dispatch(updateSiteRequired(required));
	}, [dispatch, required]);

	const handleChange = (next: SiteLocation | null) => {
		dispatch(updateSite(next));
	};

	if (!required || site.location?.id) {
		return <>{children}</>;
	}

	const isRetailOnlyGrant =
		!finish.isLoading && !finish.isError && finish.sites.length === 0 && !retail.isLoading && retail.sites.length > 0;

	if (isRetailOnlyGrant) {
		return <>{children}</>;
	}

	return (
		<Box sx={{ maxWidth: 420, mx: "auto", mt: 6, px: 2 }}>
			<Stack spacing={2}>
				<Typography variant="h5">Select a Site</Typography>
				<SelectSite value={site.location} onChange={handleChange} />
			</Stack>
		</Box>
	);
}

export default RequireSite;
